
import React, {useEffect, useState} from "react";
import {styled, useTheme} from '@mui/material/styles';
import {useNavigate, useLocation} from "react-router-dom";
import MuiDrawer from '@mui/material/Drawer';
import {Tooltip, Typography, Button, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, List} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import AppLogo from "../assets/images/oviguard-logo.png";
import {tooltipClasses} from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardIcon from "../assets/icons/dashboard.png";
import DashboardActiveIcon from "../assets/icons/dashboard-active.png";
import GeolocationIcon from "../assets/icons/geolocation.png";
import GeolocationActiveIcon from "../assets/icons/geolocation-active.png";
import AuthenticationIcon from "../assets/icons/authentication.png";
import AuthenticationActiveIcon from "../assets/icons/authentication-active.png";
import AnalyticsIcon from "../assets/icons/analytics.png";
import AnalyticsActiveIcon from "../assets/icons/analytics-active.png";
import AnomalyIcon from "../assets/icons/anomaly.png";
import AnomalyActiveIcon from "../assets/icons/anomaly-active.png";
import ReportsIcon from "../assets/icons/reports.png";
import ReportsActiveIcon from "../assets/icons/reports-active.png";

const drawerWidth = 250;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const LightTooltip = styled(({className, ...props}) => (
    <Tooltip {...props} arrow classes={{popper: className}} placement="top" />

    ))(({theme}) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#3333cf",
            color: "#fff",
            boxShadow: theme.shadows[1],
            fontSize: 11,
            fontWeight: "bold",
            padding: 7,
        },
        [`& .${tooltipClasses.arrow}`]: {
            color: "#3333cf",
        },
    }
));
  

const Sidebar = (props) => {
    const {open, handleDrawerClose} = props;

    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const [openReportsMenu, setOpenReportsMenu] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);


    useEffect(() => {
        const pathToIndexMap = {
            "/dashboard": 0,
            "/activity-logs": 0,
            "/geolocation-tracking": 1,
            "/authentication": 2,
            "/analytics": 3,
            "/anomaly-detection": 4,
            "/reports/authentication-performance": 6,
            "/reports/anomaly-detection": 7,
            "/reports/geolocation-verification": 8,
            "/reports/product-specific-verification": 9,
            "/reports/user-activity": 10,
            "/reports/verification-frequency": 11,
            "/reports/alerts-notifications": 13,
            "/reports/report-logs-list": 14
        };

        const selectedIndex = pathToIndexMap[location.pathname];

        if (selectedIndex !== undefined) {
            setSelectedIndex(selectedIndex);
        }
    }, [location.pathname, setSelectedIndex]);

    useEffect(() => {
        const reportIndices = [6, 7, 8, 9, 10, 11, 13, 14];

        if (reportIndices.includes(selectedIndex)) {
            setOpenReportsMenu(true);
        }
    }, [selectedIndex]);


    const handleOpenReportsMenu = () => {
        setOpenReportsMenu(!openReportsMenu);
    }

    const handleListMenuClick = (event, index) => {
        setSelectedIndex(index);
    
        switch (index) {
            case 0:
                navigate("/dashboard");
                setOpenReportsMenu(false);
                break;

            case 1:
                navigate("/geolocation-tracking");
                setOpenReportsMenu(false);
                break;

            case 2:
                navigate("/authentication");
                setOpenReportsMenu(false);
                break;

            case 3:
                navigate("/analytics");
                setOpenReportsMenu(false);
                break;

            case 4:
                navigate("/anomaly-detection");
                setOpenReportsMenu(false);
                break;

            case 5:
                // do nothing
                break;

            case 6:
                navigate("reports/authentication-performance");
                break;

            case 7:
                navigate("reports/anomaly-detection");
                break;

            case 8:
                navigate("reports/geolocation-verification");
                break;

            case 9:
                navigate("reports/product-specific-verification");
                break;

            case 10:
                navigate("reports/user-activity");
                break;

            case 11:
                navigate("reports/verification-frequency");
                break;

            case 12:
                navigate("reports/audit-trail");
                break;

            case 13:
                navigate("reports/alerts-notifications");
                break;

            case 14:
                navigate("reports/report-logs-list");
                break;
          
            default:
                break;
        }
    }


    return (
        <Drawer variant="permanent" open={open}>
            <DrawerHeader>
                <Button sx={{cursor: "default", mr: "auto", ...(!open && {display: 'none'})}}>
                    <img alt="oviguard-logo" src={AppLogo} style={{height: 53, marginTop: "6px", cursor:'ponter'}} onClick={() => window.location.href='/dashboard'}/>
                </Button>

                <IconButton onClick={handleDrawerClose} sx={{mt: 1}}>
                    {theme.direction === 'rtl' ?  null : <MenuIcon sx={{color: "black"}} />}
                </IconButton>
            </DrawerHeader>

            <List>
                {["Dashboard", "Geolocation Tracking", "Authentication", "Analytics", "Anomaly Detection", "Reports"].map((text, index) => (
                    <React.Fragment key={index}>
                        <LightTooltip arrow sx={{ display: (open) ? "none" : "visible" }}
                            title={
                                (index == 0) ? "Dashboard"
                                    :
                                    (index == 1) ? "Geolocation Tracking"
                                        :
                                        (index == 2) ? "Authentication"
                                            :
                                            (index == 3) ? "Analytics"
                                                :
                                                (index == 4) ? "Anomaly Detection"
                                                    :
                                                    (index == 5) ? "Reports"
                                                        :
                                                            null
                            }
                        >
                            <ListItem disablePadding sx={{display: 'block', bgcolor: (selectedIndex === index) ? "rgb(51 51 207 / 5%)" : "inherit"}}>
                                <ListItemButton sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}
                                    selected={selectedIndex === index}
                                    onClick={(index === 5) ? handleOpenReportsMenu : (e) => handleListMenuClick(e, index)}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}>
                                        {(index == 0) ?
                                            <img alt="dashboard-ico5n" src={(selectedIndex === index) ? DashboardActiveIcon : DashboardIcon} style={{height: 18}} />
                                            :
                                            (index == 1) ?
                                                <img alt="geolocation-icon" src={(selectedIndex === index) ? GeolocationActiveIcon : GeolocationIcon} style={{height: 18}} />
                                                :
                                                (index == 2) ?
                                                    <img alt="authentication-icon" src={(selectedIndex === index) ? AuthenticationActiveIcon : AuthenticationIcon} style={{height: 20}} />
                                                    :
                                                    (index == 3) ?
                                                        <img alt="analytics-icon" src={(selectedIndex === index) ? AnalyticsActiveIcon : AnalyticsIcon} style={{height: 20}} />
                                                        :
                                                        (index == 4) ?
                                                            <img alt="anomaly-icon" src={(selectedIndex === index) ? AnomalyActiveIcon : AnomalyIcon} style={{height: 20}} />
                                                            :
                                                            (index == 5) ?
                                                                <img alt="reports-icon" src={(selectedIndex === index) ? ReportsActiveIcon : ReportsIcon} style={{height: 20}} />
                                                               
                                                                    :
                                                                    null
                                        }
                                    </ListItemIcon>

                                    <ListItemText sx={{ opacity: open ? 1 : 0 }} primary={
                                        <Typography sx={{fontSize: 14, color:
                                                (selectedIndex === index) ? "#3333cf" : "#58565f"
                                        }}>
                                            {text}
                                        </Typography>
                                    } />

                                    {index === 5 && (openReportsMenu ? <ExpandLess /> : <ExpandMore />)}   
                                </ListItemButton>
                            </ListItem>
                        </LightTooltip>

                        {index === 5 && (
                            <Collapse in={openReportsMenu} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {["Authentication Performance", "Anomaly Detection", "Geolocation Verification", 
                                        "Product-specific Verification", "User Activity", "Verification Frequency", ,"Alerts and Notifications", "Report Logs"
                                    ].map((subText, subIndex) => (
                                        <ListItem key={subIndex} disablePadding sx={{bgcolor: (selectedIndex === (index + subIndex + 1)) ? "rgb(51 51 207 / 5%)" : "inherit"}}>
                                            <ListItemButton key={subIndex} sx={{pl: 4}}
                                                selected={selectedIndex === (index + subIndex + 1)}
                                                onClick={(e) => handleListMenuClick(e, index + subIndex + 1)}
                                            >
                                                <ListItemText sx={{pl: "28px"}} primary={
                                                    <Typography sx={{fontSize: 14, letterSpacing: "-0.3px", color:
                                                        (selectedIndex === (index + subIndex + 1)) ? "#3333cf" : "#58565f"
                                                    }}>
                                                        {subText}
                                                    </Typography>
                                                    } 
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        )}
                    </React.Fragment>
                ))}
            </List>
        </Drawer> 
    )
}

export default Sidebar